/**
 * How Create task's approvals list reads and writes a task's gate rows (#1753). Each tool on the task is set to one of
 * three choices, stored as the gate rows the raw Gates editor under Advanced shows:
 *
 * - Ask me first: a CEO gate, `{ tool, owner: 'ceo', approval: 'specific' }` (`specific` is the API's default).
 * - Let it run: no gate row for that tool.
 * - Ask the lead: a lead gate, `{ tool, owner: 'lead', approval: 'specific' }`, which waits for the team's lead.
 *
 * Only rows naming the tool exactly are the list's. Gate rows for anything else (a tool not on the task, one action of a
 * toolset) are left as they stand.
 */
export type GateRow = { tool: string; owner: string; approval: string }

export type Approval = 'ceo' | 'run' | 'lead'

/** What a tool's rows read as: one of the three choices, or a row someone else owns, which selects none. */
export type ApprovalState = { choice: Approval } | { choice: 'other'; owner: string }

export const APPROVAL_CHOICES: ReadonlyArray<{ value: Approval; label: string }> = [
    { value: 'ceo', label: 'Ask me first' },
    { value: 'run', label: 'Let it run' },
    { value: 'lead', label: 'Ask the lead' },
]

/** The approval the list writes for a chosen gate. */
const SPECIFIC = 'specific'

export function isApproval(value: unknown): value is Approval {
    return APPROVAL_CHOICES.some(choice => choice.value === value)
}

/**
 * The choice a tool's rows read as. Any CEO row is Ask me first, whatever its approval (the tools picker writes
 * `manual` ones), then any lead row is Ask the lead. A tool with no row runs unattended. A row owned by anyone else
 * selects none of the three, and says whose it is.
 */
export function approvalOf(gates: readonly GateRow[], tool: string): ApprovalState {
    const rows = gates.filter(gate => gate.tool === tool)
    const first = rows[0]
    if (!first) {
        return { choice: 'run' }
    }
    if (rows.some(gate => gate.owner === 'ceo')) {
        return { choice: 'ceo' }
    }
    if (rows.some(gate => gate.owner === 'lead')) {
        return { choice: 'lead' }
    }
    return { choice: 'other', owner: first.owner }
}

/**
 * The gate rows once `tool` is set to `choice`: its own rows are replaced by the one the choice stores (where the first
 * of them stood, else at the end), or dropped for Let it run. Every other row is left as it was.
 */
export function withApproval(gates: readonly GateRow[], tool: string, choice: Approval): GateRow[] {
    const at = gates.findIndex(gate => gate.tool === tool)
    const kept = gates.filter(gate => gate.tool !== tool)
    if (choice === 'run') {
        return kept
    }
    const row = { tool, owner: choice, approval: SPECIFIC }
    const index = at === -1 ? kept.length : at
    return [...kept.slice(0, index), row, ...kept.slice(index)]
}
